import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import HighlightedText from './HighlightedText';

async function fetchCategories(q) {
  if (!q) return [];
  const res = await fetch(
    `${process.env.MARKETING_API_HOST}/categories/search?query=${encodeURIComponent(q)}&limit=6`
  );
  return res.json();
}

function useClickOutside(ref, setOpen) {
  useEffect(() => {
    const element = ref.current;
    const root = element?.getRootNode?.() || document;

    const handle = (event) => {
      const path = event.composedPath();

      if (element && !path.includes(element)) {
        setOpen(false);
      }
    };

    root.addEventListener('pointerdown', handle, true);
    return () => root.removeEventListener('pointerdown', handle, true);
  }, []);
}

const CategoryField = ({ setCategoryId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [term, setTerm] = useState('');
  const [debouncedTerm] = useDebounce(term, 300);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories', debouncedTerm],
    queryFn: () => fetchCategories(debouncedTerm),
    enabled: true,
  });

  useClickOutside(ref, setOpen);

  return (
    <>
      <div className="label font-semibold mb-1">Category</div>
      <div className="relative w-full" ref={ref}>
        <input
          type="text"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          placeholder="Start typing to search..."
          className="w-full border rounded p-2"
        />
        {!isLoading && open && (
          <ul className="absolute bg-white z-40 w-full shadow">
            {categories &&
              categories.map((cat) => (
                <li
                  key={cat.id}
                  onClick={() => {
                    setTerm(cat.name);
                    setCategoryId(cat.id);
                    setOpen(false);
                  }}
                  className="p-1 hover:bg-gray-300 cursor-pointer border-b border-gray-200 last:border-none"
                >
                  <div className="text-xs text-gray-500">
                    <HighlightedText text={cat.name_with_path} query={term} />
                  </div>
                  <div className="text-sm text-gray-900">
                    <HighlightedText text={cat.name} query={term} />
                  </div>
                </li>
              ))}
            {categories.length === 0 && (
              <div className="p-4">
                Sorry we couldn't find your category. <br />
                Try a different search term.
              </div>
            )}
          </ul>
        )}
      </div>
    </>
  );
};

export default CategoryField;
